<template>
  <div class="table-panel whatsapp-groups">
    <div class="table-toolbar">
      <div class="whatsapp-groups-title">
        <strong>Grupos do WhatsApp</strong>
        <span>{{ linkedCount }} de {{ groups.length }} {{ groups.length === 1 ? 'grupo vinculado' : 'grupos vinculados' }} · mencione o bot em um grupo novo para ele aparecer aqui.</span>
      </div>
      <div class="whatsapp-groups-actions">
        <button type="button" :disabled="loading" @click="load">Atualizar</button>
        <button type="button" :disabled="syncing || !apiConfigured" :title="apiConfigured ? 'Importa os grupos em que o bot participa' : 'API do WhatsMiau não configurada no servidor'" @click="sync">
          {{ syncing ? 'Buscando...' : 'Buscar grupos do bot' }}
        </button>
        <button type="button" @click="adding = !adding">{{ adding ? 'Cancelar' : 'Adicionar por ID' }}</button>
      </div>
    </div>

    <div v-if="!loading && !botConfigured" class="whatsapp-groups-alert">
      O bot não está configurado no servidor (EVOLUTION_INSTANCE / EVOLUTION_BOT_NUMBER). Os vínculos ficam salvos, mas o bot não responderá até a configuração.
    </div>
    <div v-if="error" class="whatsapp-groups-alert is-error">
      <span>{{ error }}</span>
      <button type="button" aria-label="Fechar aviso" @click="error = ''">×</button>
    </div>
    <div v-if="notice" class="whatsapp-groups-alert is-success">
      <span>{{ notice }}</span>
      <button type="button" aria-label="Fechar aviso" @click="notice = ''">×</button>
    </div>

    <form v-if="adding" class="whatsapp-groups-add" @submit.prevent="addGroup">
      <input v-model.trim="draft.groupJid" placeholder="ID do grupo (ex.: 1203630000000000@g.us)" aria-label="ID do grupo" required>
      <input v-model.trim="draft.name" placeholder="Nome do grupo (opcional)" aria-label="Nome do grupo">
      <select v-model="draft.projectId" aria-label="Projeto" required>
        <option value="" disabled>Projeto...</option>
        <option v-for="project in sortedProjects" :key="project.id" :value="project.id">{{ project.name }}</option>
      </select>
      <button type="submit" class="is-primary" :disabled="saving">Vincular</button>
    </form>

    <table class="resource-table">
      <thead>
        <tr>
          <th>Grupo</th>
          <th>Projeto</th>
          <th>Situação</th>
          <th>Última menção</th>
          <th class="actions-col">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="group in groups" :key="group.groupJid">
          <td data-label="Grupo">
            <strong>{{ group.name || 'Grupo sem nome' }}</strong>
            <small :title="group.groupJid">{{ group.groupJid }}</small>
          </td>
          <td data-label="Projeto">
            <select
              class="whatsapp-groups-project"
              :value="group.projectId"
              :disabled="savingJid === group.groupJid"
              :aria-label="`Projeto do grupo ${group.name || group.groupJid}`"
              @change="link(group, $event.target.value)"
            >
              <option value="">Sem projeto (bot ignora)</option>
              <option v-if="group.projectId && !projectExists(group.projectId)" :value="group.projectId">Projeto removido</option>
              <option v-for="project in sortedProjects" :key="project.id" :value="project.id">{{ project.name }}</option>
            </select>
          </td>
          <td data-label="Situação">
            <span class="whatsapp-groups-status" :class="statusOf(group).tone">{{ statusOf(group).label }}</span>
          </td>
          <td data-label="Última menção">{{ group.lastSeenAt ? formatDateTime(group.lastSeenAt) : '—' }}</td>
          <td class="actions-cell" data-label="Ações">
            <button type="button" @click="copy(group.groupJid)">Copiar ID</button>
            <button v-if="group.source === 'panel'" type="button" :disabled="savingJid === group.groupJid" @click="remove(group)">Remover</button>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="loading && !groups.length" class="resource-empty">Carregando grupos...</div>
    <div v-else-if="!groups.length" class="resource-empty">
      Nenhum grupo ainda. Adicione o bot ao grupo e mencione-o (@bot) — o grupo aparecerá aqui para ser vinculado.
    </div>
  </div>
</template>

<script>
import api from '../services/api'

export default {
  name: 'WhatsappGroupsPanel',
  props: {
    projects: { type: Array, default: () => [] }
  },
  data: () => ({
    groups: [],
    botConfigured: true,
    apiConfigured: false,
    loading: false,
    syncing: false,
    saving: false,
    savingJid: '',
    adding: false,
    draft: { groupJid: '', name: '', projectId: '' },
    error: '',
    notice: ''
  }),
  computed: {
    sortedProjects () {
      return [...this.projects].sort((a, b) => String(a.name).localeCompare(String(b.name), 'pt-BR'))
    },
    linkedCount () {
      return this.groups.filter((group) => group.projectId).length
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    apply (data) {
      this.groups = data.groups || []
      this.botConfigured = data.botConfigured !== false
      this.apiConfigured = Boolean(data.apiConfigured)
    },
    async load () {
      this.loading = true
      try {
        this.apply(await api.listWhatsappGroups())
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.loading = false
      }
    },
    async sync () {
      this.syncing = true
      this.error = ''
      this.notice = ''
      try {
        const data = await api.syncWhatsappGroups()
        this.apply(data)
        this.notice = `${data.imported} ${data.imported === 1 ? 'grupo encontrado' : 'grupos encontrados'} no WhatsApp.`
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.syncing = false
      }
    },
    async link (group, projectId) {
      this.savingJid = group.groupJid
      this.error = ''
      this.notice = ''
      try {
        const saved = await api.updateWhatsappGroup(group.groupJid, { projectId })
        this.replace(saved)
        const project = this.projects.find((item) => item.id === projectId)
        this.notice = project
          ? `${group.name || 'Grupo'} vinculado a ${project.name}.`
          : `${group.name || 'Grupo'} desvinculado; o bot vai ignorá-lo.`
      } catch (error) {
        this.error = this.errorMessage(error)
        await this.load()
      } finally {
        this.savingJid = ''
      }
    },
    async addGroup () {
      this.saving = true
      this.error = ''
      this.notice = ''
      try {
        const saved = await api.updateWhatsappGroup(this.draft.groupJid, { projectId: this.draft.projectId, name: this.draft.name })
        this.replace(saved)
        this.notice = 'Grupo vinculado.'
        this.draft = { groupJid: '', name: '', projectId: '' }
        this.adding = false
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.saving = false
      }
    },
    async remove (group) {
      if (!window.confirm(`Remover o vínculo do grupo ${group.name || group.groupJid}? O bot deixará de atender esse grupo pelo painel.`)) return
      this.savingJid = group.groupJid
      try {
        await api.deleteWhatsappGroup(group.groupJid)
        await this.load()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.savingJid = ''
      }
    },
    replace (saved) {
      if (!saved) return this.load()
      const index = this.groups.findIndex((group) => group.groupJid === saved.groupJid)
      if (index >= 0) this.groups.splice(index, 1, saved)
      else this.groups.unshift(saved)
    },
    statusOf (group) {
      if (!group.projectId) return { label: 'Aguardando vínculo', tone: 'is-pending' }
      if (group.source === 'env') return { label: 'Variável de ambiente', tone: 'is-env' }
      return { label: 'Vinculado', tone: 'is-linked' }
    },
    projectExists (projectId) {
      return this.projects.some((project) => project.id === projectId)
    },
    async copy (text) {
      try {
        await navigator.clipboard.writeText(text)
        this.notice = 'ID do grupo copiado.'
      } catch (error) {
        this.error = 'Não foi possível copiar o ID.'
      }
    },
    formatDateTime (value) {
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    },
    errorMessage (error) {
      if (error.response && error.response.data && error.response.data.message) return error.response.data.message
      if (error.code === 'ECONNABORTED') return 'A API demorou para responder. Tente novamente.'
      return error.message ? `Não foi possível completar a ação: ${error.message}` : 'Não foi possível completar a ação.'
    }
  }
}
</script>

<style scoped>
.whatsapp-groups {
  margin-top: 14px;
}

.whatsapp-groups-title {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.whatsapp-groups-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}

.whatsapp-groups button,
.whatsapp-groups-add input,
.whatsapp-groups-add select,
.whatsapp-groups-project {
  min-height: 30px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: var(--panel);
  color: var(--text);
  font: inherit;
  font-size: 0.8rem;
  font-weight: 700;
}

.whatsapp-groups button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.whatsapp-groups button.is-primary {
  border-color: var(--accent);
  background: var(--accent);
  color: #ffffff;
}

.whatsapp-groups-project {
  width: 100%;
  min-width: 200px;
  max-width: 260px;
}

.whatsapp-groups-add {
  display: grid;
  grid-template-columns: minmax(200px, 1.4fr) minmax(140px, 1fr) minmax(160px, 1fr) auto;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--line);
  background: var(--panel-2);
}

.whatsapp-groups-alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 14px;
  border-bottom: 1px solid var(--line);
  background: rgba(217, 119, 6, 0.1);
  color: var(--text);
  font-size: 0.82rem;
  font-weight: 600;
}

.whatsapp-groups-alert.is-error {
  background: rgba(220, 38, 38, 0.1);
}

.whatsapp-groups-alert.is-success {
  background: rgba(22, 163, 74, 0.1);
}

.whatsapp-groups-alert button {
  min-height: 24px;
  padding: 0 8px;
  border: 0;
  background: transparent;
}

.whatsapp-groups-status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  white-space: nowrap;
}

.whatsapp-groups-status.is-linked {
  background: rgba(22, 163, 74, 0.14);
  color: #22a35a;
}

.whatsapp-groups-status.is-pending {
  background: rgba(217, 119, 6, 0.14);
  color: #d97706;
}

.whatsapp-groups-status.is-env {
  background: rgba(59, 130, 246, 0.16);
  color: #3b82f6;
}

@media (max-width: 760px) {
  .table-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .whatsapp-groups-actions {
    justify-content: flex-start;
  }

  .whatsapp-groups-add {
    grid-template-columns: 1fr;
  }
}
</style>
